import { createClient } from '@supabase/supabase-js'
import {
  mapDbPatternToPatron,
  mapDbReportToReporte,
  mapDbStyleProfileToPerfilEstilo,
  mapDbUserToUsuario,
  mapDbVoiceCorrectionToCorreccionVoz,
} from '@/lib/dbMappers'
import { crearPayloadCorreccionVoz } from '@/lib/voiceCorrectionPayload'

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) || 'https://placeholder.supabase.co'
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || 'placeholder'

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})

// ── Helpers de autenticación ──────────────────────────────────

function conTimeout<T>(promise: Promise<T>, ms = 30000): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Sin respuesta del servidor. Intenta de nuevo.')), ms)
    ),
  ])
}

export async function iniciarSesionEmail(email: string, password: string) {
  const { data, error } = await conTimeout(
    supabase.auth.signInWithPassword({ email, password })
  )
  if (error) throw error
  return data
}

export async function registrarEmail(email: string, password: string, nombre: string) {
  const { data, error } = await conTimeout(
    supabase.auth.signUp({ email, password, options: { data: { nombre } } })
  )
  if (error) throw error
  return data
}

export async function cerrarSesion() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function recuperarPassword(email: string) {
  const { error } = await conTimeout(
    supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
  )
  if (error) throw error
}

// ── Helpers de usuario ────────────────────────────────────────

export async function obtenerUsuario(userId: string) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single()
  if (error) throw error
  return mapDbUserToUsuario(data)
}

export async function actualizarUsuario(userId: string, cambios: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('users')
    .update(cambios)
    .eq('id', userId)
    .select()
    .single()
  if (error) throw error
  return mapDbUserToUsuario(data)
}

// ── Helpers de patrones ────────────────────────────────────────

export async function obtenerPatrones(autorId: string, organizacionId?: string | null) {
  const visiblePatternFilter = [
    `author_id.eq.${autorId}`,
    'is_public.eq.true',
    ...(organizacionId ? [`organization_id.eq.${organizacionId}`] : []),
  ].join(',')

  const { data, error } = await supabase
    .from('patterns')
    .select('*')
    .or(visiblePatternFilter)
    .eq('is_active', true)
    .order('use_count', { ascending: false })
  if (error) throw error
  return (data ?? []).map(row => mapDbPatternToPatron(row))
}

export async function incrementarUsoPatron(patronId: string) {
  await supabase.rpc('increment_pattern_use', { pattern_id: patronId })
}

// ── Helpers de reportes ────────────────────────────────────────

export async function guardarReporte(reporte: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('reports')
    .upsert(reporte)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function obtenerReportes(autorId: string, limite = 50) {
  const { data, error } = await supabase
    .from('reports')
    .select('*')
    .eq('author_id', autorId)
    .order('created_at', { ascending: false })
    .limit(limite)
  if (error) throw error
  return (data ?? []).map(row => mapDbReportToReporte(row))
}

// ── Helpers de correcciones de voz ────────────────────────────

export async function obtenerCorreccionesVoz(usuarioId: string) {
  const { data, error } = await supabase
    .from('voice_corrections')
    .select('*')
    .eq('user_id', usuarioId)
  if (error) throw error
  return (data ?? []).map(row => mapDbVoiceCorrectionToCorreccionVoz(row))
}

export async function guardarCorreccionVoz(params: {
  usuarioId: string
  terminoIncorrecto: string
  terminoCorrecto: string
}) {
  const { data, error } = await supabase
    .from('voice_corrections')
    .insert(crearPayloadCorreccionVoz(params))
    .select()
    .single()
  if (error) throw error
  return mapDbVoiceCorrectionToCorreccionVoz(data)
}

export async function eliminarCorreccionVoz(id: string) {
  const { error } = await supabase.from('voice_corrections').delete().eq('id', id)
  if (error) throw error
}

// ── Helpers de perfil de estilo ───────────────────────────────

export async function obtenerPerfilEstilo(usuarioId: string) {
  const { data, error } = await supabase
    .from('user_style_profiles')
    .select('*')
    .eq('user_id', usuarioId)
    .maybeSingle()
  if (error) throw error
  return mapDbStyleProfileToPerfilEstilo(data)
}

export async function guardarPerfilEstilo(perfil: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('user_style_profiles')
    .upsert(perfil)
    .select()
    .single()
  if (error) throw error
  return mapDbStyleProfileToPerfilEstilo(data)
}
